import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Sparkles, Heart, Search, Users, Star, ArrowRight, Globe, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const Landing = () => {
  const navigate = useNavigate();
  const { user, loginWithGoogle } = useAuth();
  const { t, language, changeLanguage, languages } = useLanguage();

  const handleGetStarted = () => {
    if (user) {
      navigate('/discover');
    } else {
      navigate('/register');
    }
  };

  const currentLang = languages.find(l => l.code === language);

  return (
    <div className="min-h-screen bg-[#F9F8F6] overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex flex-col">
        {/* Background */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url(https://images.unsplash.com/photo-1755003842792-9d2b7ad08862?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxmYW1pbHklMjBwYXJrJTIwcGljbmljfGVufDB8fHx8MTc3NTkxMjAxMnww&ixlib=rb-4.1.0&q=85)` }}
        />
        
        {/* Header */}
        <header className="relative z-10 flex items-center justify-between px-6 py-4 max-w-7xl mx-auto w-full">
          <div className="flex items-center gap-3">
            <video 
              src="/infobel-logo.mp4" 
              autoPlay 
              loop 
              muted 
              playsInline
              className="h-10 w-10 rounded-xl bg-[#2C302E] object-cover"
              data-testid="brand-logo"
            />
            <span className="text-2xl font-semibold text-[#2C302E] font-['Outfit']">InfoBel</span>
          </div>
          
          <nav className="flex items-center gap-4">
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className="flex items-center gap-2 px-3 py-2 rounded-full bg-white/80 border border-[#2C302E]/10 hover:bg-white transition-colors"
                  data-testid="language-selector"
                >
                  <span className="text-xl">{currentLang?.flag}</span>
                  <ChevronDown className="w-4 h-4 text-[#666B67]" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`flex items-center gap-3 cursor-pointer ${language === lang.code ? 'bg-[#E07A5F]/10' : ''}`}
                    data-testid={`lang-${lang.code}`}
                  >
                    <span className="text-xl">{lang.flag}</span>
                    <span className={language === lang.code ? 'font-medium text-[#E07A5F]' : ''}>{lang.name}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <div className="hidden md:flex items-center gap-6">
              <a href="#features" className="text-[#666B67] hover:text-[#2C302E] transition-colors">Features</a>
              {user ? (
                <Button onClick={() => navigate('/discover')} className="btn-primary" data-testid="discover-btn">
                  {t('discover_title')}
                </Button>
              ) : (
                <Button onClick={() => navigate('/login')} variant="outline" className="btn-secondary" data-testid="login-btn">
                  {t('auth_sign_in')}
                </Button>
              )}
            </div>
          </nav>
        </header>

        {/* Hero Content */}
        <div className="relative z-10 flex-1 flex items-center">
          <div className="max-w-7xl mx-auto px-6 py-12 grid lg:grid-cols-2 gap-12 items-center">
            <div className="fade-in-up">
              <span className="badge badge-secondary mb-6">{t('landing_badge')}</span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#2C302E] font-['Outfit'] leading-tight mb-6">
                {t('landing_title_1')} <span className="text-[#E07A5F]">{t('landing_title_2')}</span> {t('landing_title_3')}
              </h1>
              <p className="text-lg text-[#666B67] mb-8 max-w-lg">
                {t('landing_description')}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={handleGetStarted}
                  className="btn-primary text-lg py-6 px-8 gap-2"
                  data-testid="get-started-btn"
                >
                  {t('landing_cta_start')}
                  <ArrowRight className="w-5 h-5" />
                </Button>
                <Button 
                  onClick={() => navigate('/discover')}
                  variant="outline" 
                  className="btn-secondary text-lg py-6 px-8"
                  data-testid="explore-btn"
                >
                  {t('landing_cta_explore')}
                </Button>
              </div>
              
              {/* Trust indicators */}
              <div className="flex items-center gap-6 mt-8 text-sm text-[#666B67]">
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                  <span>4.9/5 {t('landing_rating')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#81B29A]" />
                  <span>10k+ {t('landing_families')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#E07A5F]" />
                  <span>500+ {t('landing_locations')}</span>
                </div>
              </div>
            </div>
            
            {/* Hero Image Card */}
            <div className="relative fade-in-up fade-in-up-2 hidden lg:block">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl rotate-2 hover:rotate-0 transition-transform duration-500">
                <img 
                  src="https://images.unsplash.com/photo-1755003842792-9d2b7ad08862?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxmYW1pbHklMjBwYXJrJTIwcGljbmljfGVufDB8fHx8MTc3NTkxMjAxMnww&ixlib=rb-4.1.0&q=85"
                  alt="Family enjoying outdoor activity"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 gradient-overlay p-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                      <Heart className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">Parc du Cinquantenaire</p>
                      <p className="text-white/80 text-sm">Brussel • {t('price_free')}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating cards */}
              <div className="absolute -left-8 top-20 bg-white rounded-2xl p-4 shadow-lg fade-in-up fade-in-up-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#81B29A]/10 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-[#81B29A]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#2C302E] text-sm">{t('card_trending')}</p>
                    <p className="text-xs text-[#666B67]">Pairi Daiza</p>
                  </div>
                </div>
              </div>
              
              <div className="absolute -right-4 bottom-32 bg-white rounded-2xl p-4 shadow-lg fade-in-up fade-in-up-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#E07A5F]/10 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-[#E07A5F]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#2C302E] text-sm">{t('tab_near_me')}</p>
                    <p className="text-xs text-[#666B67]">12 activiteiten</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="badge badge-primary mb-4">Why InfoBel</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2C302E] font-['Outfit'] mb-4">
              {t('features_title')}
            </h2>
            <p className="text-[#666B67] max-w-2xl mx-auto">
              {t('features_subtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Search,
                title: t('feature_smart_title'),
                description: t('feature_smart_desc'),
                color: 'bg-[#E07A5F]/10 text-[#E07A5F]'
              },
              {
                icon: MapPin,
                title: t('feature_location_title'),
                description: t('feature_location_desc'),
                color: 'bg-[#81B29A]/10 text-[#81B29A]'
              },
              {
                icon: Heart,
                title: t('feature_save_title'),
                description: t('feature_save_desc'),
                color: 'bg-amber-100 text-amber-600'
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className="bg-[#F9F8F6] rounded-3xl p-8 card-hover"
              >
                <div className={`w-14 h-14 rounded-2xl ${feature.color} flex items-center justify-center mb-6`}>
                  <feature.icon className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-semibold text-[#2C302E] mb-3 font-['Outfit']">{feature.title}</h3>
                <p className="text-[#666B67]">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Preview */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2C302E] font-['Outfit'] mb-4">
              {t('categories_title')}
            </h2>
            <p className="text-[#666B67]">{t('categories_subtitle')}</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { name: t('interest_playgrounds'), image: 'https://images.unsplash.com/photo-1755003842792-9d2b7ad08862?w=300' },
              { name: t('interest_museums'), image: 'https://images.unsplash.com/photo-1722230360984-2b0581aef534?w=300' },
              { name: t('interest_farms'), image: 'https://images.unsplash.com/photo-1771570376362-8f95dc35c2cf?w=300' },
              { name: t('interest_soft_play'), image: 'https://images.unsplash.com/photo-1566140967404-b8b3932483f5?w=300' }
            ].map((category, index) => (
              <div 
                key={index}
                onClick={() => navigate('/discover')}
                className="relative aspect-square rounded-3xl overflow-hidden cursor-pointer card-hover"
              >
                <img src={category.image} alt={category.name} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <span className="text-white font-semibold text-lg">{category.name}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-[#2C302E]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white font-['Outfit'] mb-6">
            {t('cta_title')}
          </h2>
          <p className="text-white/70 mb-8 text-lg">
            {t('cta_subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={handleGetStarted}
              className="bg-[#E07A5F] text-white hover:bg-[#C96C52] rounded-full py-6 px-8 text-lg"
              data-testid="cta-get-started"
            >
              {t('cta_button')}
            </Button>
            <Button 
              onClick={loginWithGoogle}
              variant="outline"
              className="bg-white text-[#2C302E] hover:bg-white/90 rounded-full py-6 px-8 text-lg border-0"
              data-testid="cta-google-login"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {t('cta_google')}
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-[#F9F8F6] border-t border-[#2C302E]/5">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img 
              src="/infobel-logo.png" 
              alt="InfoBel" 
              className="h-8 w-8 rounded-lg bg-[#2C302E] object-cover"
            />
            <span className="text-lg font-semibold text-[#2C302E] font-['Outfit']">InfoBel</span>
          </div>
          <p className="text-sm text-[#666B67]">
            © 2026 InfoBel. {t('footer_tagline')}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
